/** Operator console texts (owner 20.10.2026: Maria works in RU, PT, EN). */
export type OperatorLocale = 'ru' | 'pt' | 'en';

export interface OperatorMessages {
  title: string;
  email: string;
  pin: string;
  signIn: string;
  signOut: string;
  loginInvalid: string;
  loginLocked: string;
  network: string;
  online: string;
  offline: string;
  onlineHint: string;
  offlineHint: string;
  noCalls: string;
  ringing: string;
  answer: string;
  takenBy: (name: string) => string;
  waiting: (sec: number) => string;
  language: string;
  calledFrom: string;
  stageTablets: string;
  stageWhatsapp: string;
  screenTitle: string;
  extend: string;
  therapist: string;
  therapistSent: string;
  end: string;
  endsIn: (t: string) => string;
  callTaken: string;
  notifyTitle: string;
  notifyBody: (studio: string) => string;
  field: { step: string; mode: string; service: string; when: string; staff: string; extras: string; notice: string };
  steps: Record<string, string>;
  modes: Record<string, string>;
  langs: Record<string, string>;
}

const STEP_IDS = [
  'home',
  'service',
  'time',
  'extras',
  'contact',
  'confirm',
  'find',
  'hello',
  'cp',
  'cp_confirm',
  'offer',
  'gift',
  'gift_phone',
  'gift_card',
  'pay',
  'questionnaire',
  'done',
] as const;

function steps(names: string[]): Record<string, string> {
  return Object.fromEntries(STEP_IDS.map((id, i) => [id, names[i] ?? id]));
}

export const OPERATOR_MESSAGES: Record<OperatorLocale, OperatorMessages> = {
  ru: {
    title: 'Оператор 360°FACE',
    email: 'E-mail',
    pin: 'PIN',
    signIn: 'Войти',
    signOut: 'Выйти',
    loginInvalid: 'Неверный e-mail или PIN.',
    loginLocked: 'Слишком много попыток. Подождите 15 минут.',
    network: 'Нет связи с сервером. Повторяем…',
    online: 'На линии',
    offline: 'Не на линии',
    onlineHint: 'Звонки с киосков приходят сюда. Держите вкладку открытой.',
    offlineHint: 'Звонки не приходят. Клиенту сразу покажем WhatsApp студии.',
    noCalls: 'Звонков нет.',
    ringing: 'Входящий звонок',
    answer: 'Ответить',
    takenBy: (n) => `Отвечает ${n}`,
    waiting: (s) => `ждёт ${s} с`,
    language: 'Язык клиента',
    calledFrom: 'Нажал на экране',
    stageTablets: 'Мастера в студии предупреждены',
    stageWhatsapp: 'Клиенту показан WhatsApp',
    screenTitle: 'Экран клиента',
    extend: 'Продлить +10 мин',
    therapist: 'Позвать мастера',
    therapistSent: 'Мастерам отправлен сигнал',
    end: 'Завершить',
    endsIn: (t) => `До конца звонка ${t}`,
    callTaken: 'Звонок уже принял другой оператор.',
    notifyTitle: 'Звонок с киоска',
    notifyBody: (s) => `Студия ${s}: клиент просит помощь`,
    field: {
      step: 'Шаг',
      mode: 'Что делает',
      service: 'Процедура',
      when: 'Дата и время',
      staff: 'Мастер',
      extras: 'Доп. услуги и товары',
      notice: 'Ошибка на экране',
    },
    steps: steps([
      'Главное меню',
      'Выбор процедуры',
      'Выбор времени',
      'Доп. услуги',
      'Контакты',
      'Подтверждение',
      'Поиск записи',
      'Запись найдена',
      'ClassPass',
      'ClassPass — подтверждение',
      'Предложение',
      'Подарок',
      'Сертификат — телефон',
      'Сертификат — открытка',
      'Способ оплаты',
      'Анкета',
      'Готово',
    ]),
    modes: { booking: 'Новая запись', checkin: 'Пришёл по записи', classpass: 'ClassPass', gift: 'Сертификат' },
    langs: {
      pt: 'португальский',
      en: 'английский',
      fr: 'французский',
      es: 'испанский',
      ru: 'русский',
      uk: 'украинский',
      de: 'немецкий',
    },
  },
  pt: {
    title: 'Operadora 360°FACE',
    email: 'E-mail',
    pin: 'PIN',
    signIn: 'Entrar',
    signOut: 'Sair',
    loginInvalid: 'E-mail ou PIN incorretos.',
    loginLocked: 'Demasiadas tentativas. Aguarde 15 minutos.',
    network: 'Sem ligação ao servidor. A tentar de novo…',
    online: 'Disponível',
    offline: 'Indisponível',
    onlineHint: 'As chamadas dos quiosques chegam aqui. Mantenha o separador aberto.',
    offlineHint: 'Sem chamadas. O cliente vê logo o WhatsApp do estúdio.',
    noCalls: 'Sem chamadas.',
    ringing: 'Chamada a entrar',
    answer: 'Atender',
    takenBy: (n) => `Atendida por ${n}`,
    waiting: (s) => `à espera há ${s} s`,
    language: 'Língua do cliente',
    calledFrom: 'Ecrã',
    stageTablets: 'A equipa do estúdio foi avisada',
    stageWhatsapp: 'O cliente vê o WhatsApp',
    screenTitle: 'Ecrã do cliente',
    extend: 'Prolongar +10 min',
    therapist: 'Chamar terapeuta',
    therapistSent: 'Alerta enviado às terapeutas',
    end: 'Terminar',
    endsIn: (t) => `A chamada termina em ${t}`,
    callTaken: 'A chamada já foi atendida por outra pessoa.',
    notifyTitle: 'Chamada do quiosque',
    notifyBody: (s) => `Estúdio ${s}: cliente pede ajuda`,
    field: {
      step: 'Passo',
      mode: 'Está a',
      service: 'Tratamento',
      when: 'Dia e hora',
      staff: 'Terapeuta',
      extras: 'Extras e produtos',
      notice: 'Erro no ecrã',
    },
    steps: steps([
      'Menu principal',
      'Escolha do tratamento',
      'Escolha da hora',
      'Extras',
      'Contactos',
      'Confirmação',
      'Procurar marcação',
      'Marcação encontrada',
      'ClassPass',
      'ClassPass — confirmação',
      'Sugestão',
      'Oferta',
      'Cartão-presente — telefone',
      'Cartão-presente — postal',
      'Forma de pagamento',
      'Ficha',
      'Concluído',
    ]),
    modes: {
      booking: 'Nova marcação',
      checkin: 'Chegada com marcação',
      classpass: 'ClassPass',
      gift: 'Cartão-presente',
    },
    langs: { pt: 'português', en: 'inglês', fr: 'francês', es: 'espanhol', ru: 'russo', uk: 'ucraniano', de: 'alemão' },
  },
  en: {
    title: '360°FACE operator',
    email: 'E-mail',
    pin: 'PIN',
    signIn: 'Sign in',
    signOut: 'Sign out',
    loginInvalid: 'Wrong e-mail or PIN.',
    loginLocked: 'Too many attempts. Wait 15 minutes.',
    network: 'No connection to the server. Retrying…',
    online: 'Online',
    offline: 'Offline',
    onlineHint: 'Kiosk calls arrive here. Keep this tab open.',
    offlineHint: 'No calls. The customer sees the studio WhatsApp straight away.',
    noCalls: 'No calls.',
    ringing: 'Incoming call',
    answer: 'Answer',
    takenBy: (n) => `Answered by ${n}`,
    waiting: (s) => `waiting ${s} s`,
    language: 'Customer language',
    calledFrom: 'Screen',
    stageTablets: 'Studio team alerted',
    stageWhatsapp: 'Customer sees WhatsApp',
    screenTitle: 'Customer screen',
    extend: 'Extend +10 min',
    therapist: 'Call a therapist',
    therapistSent: 'Alert sent to the therapists',
    end: 'End call',
    endsIn: (t) => `Call ends in ${t}`,
    callTaken: 'Another operator already answered.',
    notifyTitle: 'Kiosk call',
    notifyBody: (s) => `Studio ${s}: a customer needs help`,
    field: {
      step: 'Step',
      mode: 'Doing',
      service: 'Treatment',
      when: 'Date and time',
      staff: 'Therapist',
      extras: 'Extras and products',
      notice: 'Error on screen',
    },
    steps: steps([
      'Main menu',
      'Choosing a treatment',
      'Choosing a time',
      'Extras',
      'Contact details',
      'Confirmation',
      'Finding a booking',
      'Booking found',
      'ClassPass',
      'ClassPass — confirm',
      'Offer',
      'Gift',
      'Gift card — phone',
      'Gift card — card',
      'Payment method',
      'Questionnaire',
      'Done',
    ]),
    modes: { booking: 'New booking', checkin: 'Arrival with booking', classpass: 'ClassPass', gift: 'Gift card' },
    langs: {
      pt: 'Portuguese',
      en: 'English',
      fr: 'French',
      es: 'Spanish',
      ru: 'Russian',
      uk: 'Ukrainian',
      de: 'German',
    },
  },
};
